import { fromCents, toCents } from './money.js';
import { CLOSURE_REASON_LABELS } from './treasury.js';
export const INVOICE_ADVANCE_STATE_LABELS = {
    'in-attesa': 'In attesa del cliente',
    'da-restituire': 'Incassata · anticipo ancora da restituire alla banca',
    'da-sistemare': 'Da sistemare · incasso del cliente mancante',
    insoluto: 'Insoluto · la fattura è ancora da incassare',
    chiuso: 'Chiuso · cliente pagato, anticipo restituito',
};
/**
 * What the advance of an invoice says: the bank repaid with «SALDO FATT.»
 * means the customer paid, so a residual left there is a collection to
 * reconcile; an unpaid takes the advance back and the invoice stays open.
 */
export function invoiceAdvanceSummary(invoice, advances) {
    const sum = (amounts) => amounts.reduce((total, value) => total + toCents(value), 0);
    const closures = advances.flatMap((advance) => advance.closures);
    const of = (kinds) => sum(closures.filter((item) => kinds.includes(item.kind)).map((item) => item.amount));
    const advanced = sum(advances.map((advance) => advance.advanced));
    const owed = sum(advances.map((advance) => advance.open));
    const residual = toCents(invoice.residual);
    const net = toCents(invoice.netDue);
    const unpaid = of(['insoluto']);
    const repaid = closures.some((item) => item.kind === 'rimborso');
    const state = unpaid > 0 && residual > 0
        ? 'insoluto'
        : owed <= 0 && residual <= 0
            ? 'chiuso'
            : owed <= 0 && repaid
                ? 'da-sistemare'
                : residual <= 0
                    ? 'da-restituire'
                    : 'in-attesa';
    return {
        state,
        advanced: fromCents(advanced),
        percent: net > 0 ? Math.round((advanced * 100) / net) : 0,
        returned: fromCents(of(['rimborso', 'estinzione', 'trattenuto'])),
        unpaid: fromCents(unpaid),
        owed: fromCents(owed),
        collected: fromCents(net - residual),
        residual: fromCents(residual),
    };
}
const negative = (amount) => fromCents(-Math.abs(toCents(amount)));
/**
 * Every movement of an anticipated invoice, the oldest first; with the
 * invoice, it opens the list, so that the column of the invoice adds up to
 * what is left to collect.
 */
export function invoiceAdvanceTimeline(advances, settlements, invoice) {
    const rows = invoice
        ? [
            {
                key: 'f',
                date: invoice.date,
                kind: 'fattura',
                text: `Fattura ${invoice.number}: netto da incassare`,
                movement: null,
                invoice: invoice.netDue,
                bank: null,
                batch: null,
            },
        ]
        : [];
    for (const advance of advances) {
        rows.push({
            key: `a|${advance.movementId}`,
            date: advance.date,
            kind: 'anticipo',
            text: advance.reference ?? 'Anticipo della banca',
            movement: { id: advance.movementId, accountCode: advance.accountCode },
            invoice: null,
            bank: advance.advanced,
            batch: advance.invoices && advance.invoices > 1 && advance.movementAdvanced
                ? { invoices: advance.invoices, advanced: advance.movementAdvanced }
                : null,
        });
        for (const closure of advance.closures) {
            // What the bank kept is a collection of the invoice: it comes with the settlements;
            // unless it is the collection of the invoice issued again after a credit note (2.17).
            if (closure.kind === 'trattenuto') {
                if (!closure.fromInvoice)
                    continue;
                rows.push({
                    key: `c|${advance.movementId}|${closure.movementId}`,
                    date: closure.date,
                    kind: 'trattenuto',
                    text: `Incasso della FT ${closure.fromInvoice.number} trattenuto dalla banca per questo anticipo, senza movimento sul conto`,
                    movement: null,
                    invoice: null,
                    bank: negative(closure.amount),
                    batch: null,
                });
                continue;
            }
            rows.push({
                key: `c|${advance.movementId}|${closure.movementId}`,
                date: closure.date,
                kind: closure.kind,
                text: closure.kind === 'rimborso'
                    ? 'Rimborso dell’anticipo alla banca'
                    : closure.kind === 'insoluto'
                        ? 'Insoluto: la banca si riprende l’anticipo'
                        : 'Anticipo estinto da noi',
                movement: { id: closure.movementId, accountCode: advance.accountCode },
                invoice: null,
                bank: negative(closure.amount),
                batch: null,
            });
        }
    }
    for (const settlement of settlements) {
        const base = {
            key: `s|${settlement.id}`,
            date: settlement.date,
            invoice: negative(settlement.amount),
            movement: settlement.movement
                ? { id: settlement.movement.id, accountCode: settlement.movement.accountCode }
                : null,
            batch: null,
        };
        if (settlement.retained) {
            rows.push({
                ...base,
                kind: 'trattenuto',
                text: 'Incasso trattenuto dalla banca per l’anticipo, senza movimento sul conto',
                bank: negative(settlement.amount),
            });
        }
        else if (settlement.movement) {
            rows.push({ ...base, kind: 'incasso', text: settlement.movement.description, bank: null });
        }
        else {
            rows.push({
                ...base,
                kind: 'altro',
                text: settlement.creditLink
                    ? `Nota di credito ${settlement.creditLink.otherNumber}`
                    : settlement.compensation
                        ? `Compensata con la fattura ${settlement.compensation.otherNumber}`
                        : settlement.closure
                            ? `Chiusa senza movimento · ${CLOSURE_REASON_LABELS[settlement.closure.reason]}${settlement.note ? ` · ${settlement.note}` : ''}`
                            : settlement.writeOff
                                ? 'Abbuono'
                                : `Segnato a mano${settlement.note ? ` · ${settlement.note}` : ''}`,
                bank: null,
            });
        }
    }
    const order = {
        fattura: -1,
        anticipo: 0,
        incasso: 1,
        trattenuto: 2,
        altro: 3,
        rimborso: 4,
        estinzione: 5,
        insoluto: 6,
    };
    return rows.sort((a, b) => a.date.localeCompare(b.date) || order[a.kind] - order[b.kind]);
}
//# sourceMappingURL=invoice-advance.js.map